import { FC, useEffect } from 'react';

import { ScenarioHooks, useLocale } from '../../../../../hooks/index';
import { ScenarioType } from '../../../../../types/scenario-modeling.types';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../cap-table.store';
import { CapTableTab, Column } from '../liquidity.types';
import { TabProps, TotalRow } from '../table-components/index';
import { ShareClassTableItem } from './share-class-item';

const emptyCell = { value: '-', change: null };

export const ShareClassesTab: FC<TabProps> = ({ visibleColumns, scenarioId }) => {
  const { messagesLocale } = useLocale();
  const { data, isEmptyState } = ScenarioHooks.useCapTableShareClasses<ScenarioType.LIQUIDITY>({
    includeSearch: true,
  });

  const setEmptySearch = useScenarioCapTableStore(({ setEmptySearch }) => setEmptySearch);

  const { modifiedShareClasses } = ScenarioHooks.useModifiedShareClasses({ scenarioId });
  const modifiedShareClassesIds =
    modifiedShareClasses?.map(({ shareClass }) => shareClass.id) || [];

  const { data: summarizeData } = ScenarioHooks.useCapTableSummarize<ScenarioType.LIQUIDITY>();

  const {
    avgSharePrice,
    diluted,
    dilutedPercentage,
    investment,
    issued,
    issuedPercentage,
    votingPercentage,
    voting,
    capParticipating,
    commonStockProceeds,
    effectiveLiquidityProceeds,
    minLiquidityProceeds,
    multiple,
    participatingOnProceeds,
    proceedsWithoutLiquidity,
    seniorityInfo,
  } = summarizeData || {};

  const senioritiesFromSummarize = Object.values(seniorityInfo || {});

  const seniorities = senioritiesFromSummarize.length
    ? senioritiesFromSummarize
    : data?.[0]?.seniorityColumns || [];

  useEffect(() => {
    if (!data) return;
    setEmptySearch(isEmptyState);
  }, [data, isEmptyState, setEmptySearch]);

  if (!summarizeData || !data) return null;

  return (
    <>
      {data?.map(
        (
          {
            stakeholders,
            diluted,
            dilutedPercentage,
            investment,
            issued,
            issuedPercentage,
            votingPercentage,
            name,
            voting,
            capParticipating,
            commonStockProceeds,
            effectiveLiquidityProceeds,
            minLiquidityProceeds,
            multiple,
            participatingOnProceeds,
            proceedsWithoutLiquidity,
            investmentDays,
            seniorityColumns,
            shareClass,
          },
          i,
        ) => {
          return (
            <ShareClassTableItem
              fields={{
                [Column.NAME]: { value: name, change: null },
                [Column.ISSUED]: {
                  change: null,
                  value: issuedPercentage * 100,
                },
                [Column.DILUTED]: {
                  value: dilutedPercentage * 100,
                  change: null,
                },
                [Column.VOTING]: {
                  value: votingPercentage * 100,
                  change: null,
                },

                [Column.NO_ISSUED]: {
                  change: null,
                  value: issued,
                },
                [Column.DILUTED_SHARES]: {
                  change: null,
                  value: diluted,
                },

                [Column.VOTING_RIGHTS]: {
                  change: null,
                  value: voting,
                },
                [Column.INVESTMENT]: {
                  change: null,
                  value: investment,
                },
                [Column.AVERAGE_SHARE_PRICE]: {
                  change: null,
                  value: '-',
                },

                [Column.CAP_PARTICIPATING]: { value: capParticipating, change: null },
                [Column.MULTIPLE]: { value: multiple, change: null },
                [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
                  change: null,
                  value: proceedsWithoutLiquidity,
                },
                [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: minLiquidityProceeds },
                [Column.PARTICIPATING_ON_PROCEEDS]: {
                  value: toMathRound(participatingOnProceeds, 0),
                  change: null,
                },
                [Column.COMMON_STOCK_PROCEEDS]: { value: commonStockProceeds, change: null },
                [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
                  change: null,
                  value: effectiveLiquidityProceeds,
                },
                [Column.INVESTMENT_TIME]: { change: null, value: investmentDays },
                [Column.SENIORITY]: {
                  change: null,
                  value: seniorityColumns?.map(({ seniorityValue }) => seniorityValue),
                },
                [Column.ROI]: { value: '-', change: null },
              }}
              isModified={modifiedShareClassesIds.includes(shareClass?.id)}
              items={stakeholders}
              key={`${name}_${i}`}
              visibleColumns={visibleColumns}
            />
          );
        },
      )}

      <TotalRow
        fields={{
          [Column.INVESTMENT_TIME]: null,
          [Column.NAME]: { value: messagesLocale['TOTAL'], change: null },
          [Column.ISSUED]: {
            change: null,
            value: toNumber(issuedPercentage) * 100,
          },
          [Column.DILUTED]: {
            value: toNumber(dilutedPercentage) * 100,
            change: null,
          },
          [Column.VOTING]: {
            value: toNumber(votingPercentage) * 100,
            change: null,
          },

          [Column.NO_ISSUED]: {
            change: null,
            value: toNumber(issued),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: toNumber(diluted),
          },
          [Column.VOTING_RIGHTS]: {
            change: null,
            value: toNumber(voting),
          },
          [Column.INVESTMENT]: {
            change: null,
            value: toNumber(investment),
          },
          [Column.AVERAGE_SHARE_PRICE]: {
            change: null,
            value: toNumber(avgSharePrice),
          },
          [Column.CAP_PARTICIPATING]: { value: toNumber(capParticipating), change: null },
          [Column.MULTIPLE]: { value: toNumber(multiple), change: null },
          [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
            change: null,
            value: toNumber(proceedsWithoutLiquidity),
          },
          [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: toNumber(minLiquidityProceeds) },
          [Column.PARTICIPATING_ON_PROCEEDS]: {
            value: toMathRound(participatingOnProceeds, 0),
            change: null,
          },
          [Column.COMMON_STOCK_PROCEEDS]: { value: toNumber(commonStockProceeds), change: null },
          [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
            change: null,
            value: toNumber(effectiveLiquidityProceeds),
          },
          [Column.SENIORITY]: {
            change: null,
            value: Object.values(seniorities).map(({ seniorityValue }) => seniorityValue),
          },
          [Column.ROI]: emptyCell,
        }}
        tab={CapTableTab.BY_SHARE_CLASS}
        visibleColumns={visibleColumns}
      />
    </>
  );
};
